import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  nodeSshSchema,
  sshProbeResultSchema,
  sshSessionSchema,
  type NodeSsh,
  type SshProbeResult,
  type SshSession,
  type TrustHostKey,
  type UpdateNodeSsh,
} from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest } from '../../shared/api/client';

export const sshSettingsKey = (nodeId: string) => ['ssh', nodeId] as const;
export const sshPermittedNodesKey = ['ssh', 'permitted-nodes'] as const;

/** Параметры подключения узла и результат последнего зондирования. */
export function useNodeSsh(nodeId: string | null) {
  return useQuery({
    queryKey: sshSettingsKey(nodeId ?? ''),
    enabled: nodeId !== null,
    queryFn: () => apiRequest<NodeSsh>(`/nodes/${nodeId ?? ''}/ssh`, nodeSshSchema),
  });
}

/**
 * Изменение параметров подключения.
 *
 * Оптимистично, как и у VNC, и по той же причине: флажок «устаревшие
 * алгоритмы» иначе не двигается под пальцем до конца круга к серверу,
 * и человек нажимает второй раз, решив, что промахнулся.
 */
export function useUpdateNodeSsh(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateNodeSsh) =>
      apiRequest(`/nodes/${nodeId}/ssh`, nodeSshSchema, { method: 'PUT', body: input }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: sshSettingsKey(nodeId) });

      const previous = queryClient.getQueryData<NodeSsh>(sshSettingsKey(nodeId));

      queryClient.setQueryData(sshSettingsKey(nodeId), (current?: NodeSsh) =>
        current ? { ...current, ...input } : current,
      );

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(sshSettingsKey(nodeId), context.previous);
      }
    },
    onSuccess: (settings) => queryClient.setQueryData(sshSettingsKey(nodeId), settings),
  });
}

/**
 * Зондирование по кнопке.
 *
 * Ключ хоста в кэш параметров отсюда **не** попадает: зондирование его
 * читает, но не закрепляет (ADR 026). В ответе он есть — его показывает
 * панель сведений, — а закреплённым значением остаётся прежнее.
 */
export function useProbeSsh(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiRequest<SshProbeResult>(`/nodes/${nodeId}/ssh/probe`, sshProbeResultSchema, {
        method: 'POST',
      }),
    onSuccess: (result) =>
      queryClient.setQueryData(sshSettingsKey(nodeId), (previous?: NodeSsh) =>
        previous
          ? {
              ...previous,
              banner: result.banner,
              authMethods: result.authMethods,
              lastProbeAt: result.at,
              lastProbeError: result.error,
            }
          : previous,
      ),
  });
}

/** Подтверждение смены ключа хоста: право `credential:write`. */
export function useTrustHostKey(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: TrustHostKey) =>
      apiRequest(`/nodes/${nodeId}/ssh/trust-host-key`, nodeSshSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (settings) => queryClient.setQueryData(sshSettingsKey(nodeId), settings),
  });
}

/**
 * Открытие сессии терминала.
 *
 * Обычная функция, а не хук, — по той же причине, что и у VNC: тикет
 * одноразовый и живёт тридцать секунд, поэтому берёт его тот, кто
 * подключается, и в момент подключения.
 */
export async function openSshSession(nodeId: string): Promise<SshSession> {
  return apiRequest<SshSession>(`/nodes/${nodeId}/ssh/session`, sshSessionSchema, {
    method: 'POST',
  });
}

/** Закрытие сессии, которую так и не открыли. */
export async function closeSshSession(sessionId: string): Promise<void> {
  await apiRequest(`/sessions/${sessionId}`, z.unknown(), {
    method: 'DELETE',
    acceptStatuses: [204],
  });
}

/** Узлы, на которых право открывать терминал выдано точечно (§7.6). */
export function useSshPermittedNodes(enabled = true) {
  return useQuery({
    queryKey: sshPermittedNodesKey,
    enabled,
    staleTime: 60_000,
    queryFn: () => apiRequest('/ssh/permitted-nodes', z.object({ nodeIds: z.array(z.uuid()) })),
  });
}
